import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { trackerListAuditLogs } from '../../utils/tracker';
import type { TrackerAuditLogEntry } from '../../utils/tracker';
const TRACKER_ACTION_TYPE_OPTIONS = ['', 'login', 'admin-user-list', 'admin-user-upsert', 'admin-user-delete', 'admin-audit-log-list', 'State Backup', 'Contributor Added', 'Contributor Removed', 'Solution Added', 'Solution Removed', 'Availability Updated', 'Experience Updated', 'Date Availability Updated', 'Daily Availability Updated'];
const PAGE_SIZE = 50;
function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}
function truncate(str: unknown, max = 80): string {
  if (str === null || str === undefined) return '—';
  const s = typeof str === 'string' ? str : JSON.stringify(str);
  return s.length > max ? s.substring(0, max) + '...' : s;
}
function extractDetails(log: TrackerAuditLogEntry): Record<string, unknown> {
  return Object.entries(log).filter(([k]) => !['_id', 'timestamp', 'actionType', 'actorId', 'accessLevel'].includes(k)).reduce((acc, [k, v]) => ({
    ...acc,
    [k]: v
  }), {});
}
function CopyButton({
  text
}: {
  text: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select text for manual copy
    }
  };
  return <button onClick={e => {
    e.stopPropagation();
    void handleCopy();
  }} className="ml-1.5 p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors inline-flex flex-shrink-0" title="Copy details" data-id="element-557" data-mp="2zjw3tzr2sp4b">
      {copied ? <Check size={12} className="text-emerald-500" data-id="element-558" data-mp="2kw5jj1q0ab30" /> : <Copy size={12} data-id="element-559" data-mp="2ozk322d5l9l7" />}
    </button>;
}
const trackerLogColumns: SortableColumn<TrackerAuditLogEntry>[] = [{
  key: 'timestamp',
  label: 'Timestamp',
  width: '20%',
  render: row => <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-560" data-mp="2fxuagt7jc4lu">{formatTimestamp(row.timestamp)}</span>,
  sortValue: row => row.timestamp
}, {
  key: 'actionType',
  label: 'Action',
  width: '18%',
  render: row => <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700" data-id="element-561" data-mp="38mi010zbhg57">
        {row.actionType}
      </span>,
  sortValue: row => row.actionType
}, {
  key: 'actorId',
  label: 'Actor',
  width: '18%',
  render: row => <span className="text-xs text-slate-700 font-medium" data-id="element-562" data-mp="3mmiyyaw0udi1">{row.actorId || '—'}</span>,
  sortValue: row => row.actorId || ''
}, {
  key: 'details',
  label: 'Details',
  width: '44%',
  render: row => <div className="flex items-center text-xs text-slate-500 max-w-md" data-id="element-563" data-mp="1fx8ykqi5036v">
        <span className="truncate flex-1 min-w-0" data-id="element-564" data-mp="1azyy8ft7m749">
          {truncate(extractDetails(row), 120)}
        </span>
        <CopyButton text={JSON.stringify(extractDetails(row), null, 2)} data-id="element-565" data-mp="1opso91ggaxfk" />
      </div>
}];
export function TrackerLogTab() {
  const [logs, setLogs] = useState<TrackerAuditLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        offset,
        limit: PAGE_SIZE
      };
      if (filterAction) params.actionType = filterAction;
      if (filterActor) params.actorEmail = filterActor;
      if (filterStartDate) params.startDate = filterStartDate;
      if (filterEndDate) params.endDate = filterEndDate;
      const data = await trackerListAuditLogs(params);
      setLogs(data.logs);
      setTotalCount(data.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tracker audit logs');
    } finally {
      setLoading(false);
    }
  }, [offset, filterAction, filterActor, filterStartDate, filterEndDate]);
  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const applyFilters = () => {
    setOffset(0);
  };
  return <div className="space-y-4" data-id="element-566" data-mp="e9n5vngd2175">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-567" data-mp="i9e1c4gu15i0">
        <div data-id="element-568" data-mp="i9x9itgokfiu">
          <h3 className="text-base font-semibold text-slate-900" data-id="element-569" data-mp="i7b2qw1m641o">Tracker Log</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-570" data-mp="22t14w2jwlna2">
            Resource Tracker activity trail ({totalCount} total entries)
          </p>
        </div>
        <button onClick={() => void loadLogs()} disabled={loading} className="p-2 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50" title="Refresh" data-id="element-571" data-mp="1tq8avrjc6xgj">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} data-id="element-572" data-mp="2mhrx7kb8crmr" />
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-id="element-573" data-mp="2cpm8cjg9yqbs">
        <div className="grid grid-cols-4 gap-3" data-id="element-574" data-mp="2cnt398lb8e5w">
          <div data-id="element-575" data-mp="27pri8i4bq4rs">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-576" data-mp="27pzss3cz4xzt">Action Type</label>
            <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-577" data-mp="356ykzlqvs9ff">
              <option value="" data-id="element-578" data-mp="12i8627o7jeak">All actions</option>
              {TRACKER_ACTION_TYPE_OPTIONS.filter(Boolean).map(action => <option key={action} value={action} data-id="element-579" data-mp="oq0o3m1l1rvx">{action}</option>)}
            </select>
          </div>
          <div data-id="element-580" data-mp="etxbfm93uauq">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-581" data-mp="etfhwxdtuaqi">Actor Email</label>
            <input type="text" value={filterActor} onChange={e => setFilterActor(e.target.value)} placeholder="Search by email..." className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-582" data-mp="jss034tr6tmr" />
          </div>
          <div data-id="element-583" data-mp="256cykp3uwpm7">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-584" data-mp="256wigbd2ho2v">Start Date</label>
            <input type="datetime-local" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-585" data-mp="2fa1rew1t1hbc" />
          </div>
          <div data-id="element-586" data-mp="2jalq44210szl">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-587" data-mp="2j9sil0v8tzge">End Date</label>
            <input type="datetime-local" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-588" data-mp="2y2vzr74xab6l" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2" data-id="element-589" data-mp="1cohpu8kx11io">
          <button onClick={applyFilters} className="px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-590" data-mp="18luw224op6iu">
            Apply Filters
          </button>
          <button onClick={() => {
          setFilterAction('');
          setFilterActor('');
          setFilterStartDate('');
          setFilterEndDate('');
          setOffset(0);
        }} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-591" data-mp="224ulosp5fhva">
            Clear
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-id="element-592" data-mp="1n8c850tkslt0">
          {error}
        </div>}

      {/* Table */}
      {loading && logs.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-593" data-mp="2yok2ieyusqtl">
          Loading tracker audit logs...
        </div> : logs.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-594" data-mp="3dqiu16hqj193">
          No tracker audit log entries found.
        </div> : <SortableTable columns={trackerLogColumns} data={logs} rowKey={row => row._id} defaultSort={{
      key: 'timestamp',
      dir: 'desc'
    }} data-id="element-595" data-mp="38shakkgeczx3" />}

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between text-sm text-slate-500" data-id="element-596" data-mp="2v0u1tl59pyxf">
          <span data-id="element-597" data-mp="2q17qjh902sr9">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <div className="flex items-center gap-1" data-id="element-598" data-mp="2l1cyhadhnx4m">
            <button onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-599" data-mp="2l47kh6c5hq05">
              <ChevronLeft size={16} data-id="element-600" data-mp="f9oc1hegg51b" />
            </button>
            <span className="px-2 text-xs" data-id="element-601" data-mp="3cdiitx1qhb4a">
              Page {Math.floor(offset / PAGE_SIZE) + 1} of {totalPages}
            </span>
            <button onClick={() => setOffset(o => Math.min((totalPages - 1) * PAGE_SIZE, o + PAGE_SIZE))} disabled={offset >= (totalPages - 1) * PAGE_SIZE} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-602" data-mp="38bol9xmu4n8c">
              <ChevronRight size={16} data-id="element-603" data-mp="2tlhdcbugemlw" />
            </button>
          </div>
        </div>}
    </div>;
}